import { TOOLS_IDS } from './constants';
import getAnnotateToolIdFromKonvaNode from './getAnnotateToolIdFromKonvaNode';
import {
  TEMPLATE_CARD_BACKGROUND_KEY,
  findTextIdForCardBackground,
  getTextBackgroundCardTextId,
  isTextBackgroundCardGroup,
} from './annotationGroupUtils';

const pointInAxisAlignedRect = (px, py, rect) => {
  const x = rect.x ?? 0;
  const y = rect.y ?? 0;
  const width = rect.width ?? 0;
  const height = rect.height ?? 0;
  return px >= x && px <= x + width && py >= y && py <= y + height;
};

const resolveTemplateCardHit = (annotations, cardAnnotation, cardId) => {
  const textId =
    (cardAnnotation.groupId &&
      isTextBackgroundCardGroup(annotations, cardAnnotation.groupId) &&
      getTextBackgroundCardTextId(annotations, cardAnnotation.groupId)) ||
    findTextIdForCardBackground(annotations, cardId);

  if (!textId) {
    return null;
  }

  return { toolId: TOOLS_IDS.TEXT, annotationId: textId };
};

/**
 * Map a Konva hit target to Draw tool + annotation id.
 * Template card rects resolve to their partner text layer.
 */
export const resolveAnnotateHitFromKonvaNode = (node, annotations = {}) => {
  const hit = getAnnotateToolIdFromKonvaNode(node);
  if (!hit) {
    return null;
  }

  const annotation = annotations[hit.annotationId];
  if (annotation?.[TEMPLATE_CARD_BACKGROUND_KEY]) {
    return resolveTemplateCardHit(annotations, annotation, hit.annotationId);
  }

  return hit;
};

/**
 * Resolve an existing Draw annotation under the pointer so Text-tool preview
 * does not start on top of template stickers or other layers.
 */
export const findAnnotateToolHitAtPointer = (
  stage,
  target,
  annotations = {},
  designLayer,
) => {
  const directHit = resolveAnnotateHitFromKonvaNode(target, annotations);
  if (directHit) {
    return directHit;
  }

  const pointer = stage?.getPointerPosition?.();
  if (!pointer) {
    return null;
  }

  const intersectionHit = resolveAnnotateHitFromKonvaNode(
    stage.getIntersection(pointer),
    annotations,
  );
  if (intersectionHit) {
    return intersectionHit;
  }

  const layerPos =
    designLayer?.getRelativePointerPosition?.() ??
    stage?.getRelativePointerPosition?.();
  if (!layerPos) {
    return null;
  }

  for (const annotation of Object.values(annotations)) {
    if (!annotation?.[TEMPLATE_CARD_BACKGROUND_KEY]) {
      continue;
    }
    if (!pointInAxisAlignedRect(layerPos.x, layerPos.y, annotation)) {
      continue;
    }
    const cardHit = resolveTemplateCardHit(
      annotations,
      annotation,
      annotation.id,
    );
    if (cardHit) {
      return cardHit;
    }
  }

  return null;
};

export default resolveAnnotateHitFromKonvaNode;
